import { remove, isEqual, pick } from 'lodash-es';
import { reportError } from './utils/errorReporter';

const RELOADABLE_ERROR_STATUSES = [401, 403];
const shouldReload = (errorXHR, abortReload = false) => {
  if (abortReload) {
    return false;
  }

  return (
    RELOADABLE_ERROR_STATUSES.includes(errorXHR.status) ||
    (errorXHR.status === 422 && errorXHR.responseJSON && errorXHR.responseJSON.action === 'reload')
  );
};
const handleError = (errorXHR, errorMessage, abortReload = false) => {
  const status = errorXHR.status;

  if (shouldReload(errorXHR, abortReload)) {
    window.location.reload();
  } else {
    const reportableError = {
      error: errorMessage,
      params: {
        status,
        body: errorXHR.responseText,
        statusText: errorXHR.statusText,
        responseHeaders: errorXHR.getAllResponseHeaders?.(),
      },
    };

    reportError(reportableError);
  }
};
const currentRequests = [];
const request = (url, type = 'GET', data = {}, options = {}) => {
  if (options.abortDuplicateRequests) {
    for (const request of currentRequests) {
      if (request.type === type) {
        if (
          request.url === url ||
          request.url === `${url}.json` ||
          (options.identifier && request.identifier === options.identifier)
        ) {
          // Some widgets use the same entrypoint but with different query params.
          // So that we can't deeply compare request data, but the specific fields like 'limit' only.
          // Example:
          //  /widgets/tools_ranking?period=month&limit=10 /widgets/tools_ranking?period=week&limit=10 => abort
          //  /widgets/tools_ranking?period=month&limit=10 /widgets/tools_ranking?period=month&limit=20 => not abort
          // If abortByIdentifier is explicitly set, always abort by identifier regardless of params.
          if (options.abortByIdentifier && options.identifier && request.identifier === options.identifier) {
            request.xhr.abort();
          } else if (options.skippableAbortParams) {
            if (isEqual(pick(request.data, options.skippableAbortParams), pick(data, options.skippableAbortParams))) {
              request.xhr.abort();
            }
          } else {
            request.xhr.abort();
          }
        }
      }
    }
  }

  url = formatUrl(url);

  let currentRequest = {
    url,
    type,
    data,
    dataType: 'json',
  };

  if (type !== 'GET') {
    // Allow to upload files via FormData. Sample: new FormData()['file'] = files[0]
    if (data instanceof FormData) {
      currentRequest.contentType = false;
      currentRequest.processData = false;
      currentRequest.data = data;
    } else {
      currentRequest.data = JSON.stringify(data);
      currentRequest.contentType = 'application/json';
    }
  }

  currentRequest = Object.assign(currentRequest, options);
  currentRequest.xhr = $.ajax(currentRequest);

  currentRequests.push(currentRequest);

  return new Promise((resolve, reject) => {
    currentRequest.xhr
      .then(resolve)
      .fail((errorXHR, errorMessage) => {
        if (!(errorXHR.status === 0 && errorXHR.statusText === 'abort')) {
          handleError(errorXHR, errorMessage, options.abortReload);
        }
        reject(errorXHR);
      })
      .always(() => {
        remove(currentRequests, (request) => request === currentRequest);
      });
  });
};

function formatUrl(url) {
  if (url.match('.json')) {
    return url;
  }
  // FIXME: Implement with URL when we drop IE support:
  // import baseUrl from '~/utils/baseUrl';
  //
  // let parsedUrl;
  //
  // if (url.match(/^http/)) {
  //   parsedUrl = new URL(url);
  //   parsedUrl.pathname = parsedUrl.pathname + '.json';
  //   return parsedUrl.toString();
  // } else {
  //   parsedUrl = new URL(url, baseUrl());
  //   parsedUrl.pathname = parsedUrl.pathname + '.json';
  //   return parsedUrl.pathname + parsedUrl.search;
  // }
  const parts = url.split('?', 2);

  parts[0] = parts[0] + '.json';

  if (parts[1]) {
    parts[1] = '?' + parts[1];
  }

  return parts.join('');
}

export default {
  request,
  currentRequests,
  get(url, params = {}, options = {}) {
    return request(url, 'GET', params, options);
  },
  post(url, params = {}, options = {}) {
    return request(url, 'POST', params, options);
  },
  patch(url, params = {}, options = {}) {
    return request(url, 'PATCH', params, options);
  },
  delete(url, params = {}, options = {}) {
    return request(url, 'DELETE', params, options);
  },
  isAbortedRequest(request) {
    return request && request.status === 0 && request.statusText === 'abort';
  },
};
